import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { Button, Divider, Dropdown, DropdownItemProps, Form, Header, Input, Segment, Select, Tab, Table } from "semantic-ui-react";
import _ from 'lodash';
import "../../../assets/scss/components/TestingOverview.scss";
import ReactDatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.min.css';
import moment from "moment";
import * as d3 from "d3";
import $ from "jquery";
import { hostAbsoluteUrl, requestMeasurementNameAndType, restRequest } from "../../../http";
import { DATA_TYPE_SCALAR, DATA_TYPE_ARRAY, DATA_TYPE_VECTOR, OverviewDaily, OverviewGeneral, OverviewMeasurement, MeasurementPlotInfo, UploadStatus, OverviewSelections } from "../../../models/testing_model";
import { useCurrentUser } from "../../../hooks/auth-hook";
import { ScatterPlot } from "./ScatterPlot";
import { VectorPlot } from "./VectorPlot";
import { TestingVisualSearchParameterContext } from "../../../context";
import { compareMeasurementName, formatDateTime, parseQueryObject, toUrlQueryString } from "../../../utils";
import { MyBarChart } from "../../commons/MyBarChart";
import { DropdownFilter, MultipleDropdownFilter } from "../../commons/MyDropdown";
const downloadCsv = require('download-csv') as any;

export function TestingOverview(props: { id: number }) {
    const { id } = props;
    const changedId = useState(id);
    const dataReady = useState(false);
    const currentUser = useCurrentUser();
    const searchBarRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number>();
    const sLoading = useState(false);

    const [searchParameters, setParentSearchParameter] = useContext(TestingVisualSearchParameterContext)!;

    const start_time = _.get(searchParameters, 'start_time', '');
    const end_time = _.get(searchParameters, 'end_time', '');
    const sSearch = useState<{ status: number[], serial_number?: string, start_time: Date | null, end_time: Date | null, testing_station_id: string[], employee_id: string[], hardware_version: string[], never_pass?: number, never_pass_other_station?: number, stock_in?: number, by_sequence: string, has_refurbishment_serial: boolean, mp_batch_serial: string[], custom_filter_ids: number[] }>({
        status: _.get(searchParameters, 'status', '').split('_').map(s => parseInt(s)).filter(v => !_.isNil(v) && !_.isNaN(v)),
        serial_number: _.get(searchParameters, 'serial_number', ''),
        start_time: start_time ? moment(start_time, 'YYYY-MM-DD HH:mm:ss').toDate() : null,
        end_time: end_time ? moment(end_time, 'YYYY-MM-DD HH:mm:ss').toDate() : null,
        testing_station_id: _.get(searchParameters, 'testing_station_id', '').split('_-_').filter(v => !_.isEmpty(v)),
        employee_id: _.get(searchParameters, 'employee_id', '').split('_-_').filter(v => !_.isEmpty(v)),
        never_pass: parseInt(_.get(searchParameters, 'never_pass', '')) || undefined,
        never_pass_other_station: parseInt(_.get(searchParameters, 'never_pass_other_station', '')),
        stock_in: parseInt(_.get(searchParameters, 'stock_in', '')) || undefined,
        by_sequence: _.get(searchParameters, 'by_sequence', ''),
        has_refurbishment_serial: !!_.get(searchParameters, 'has_refurbishment_serial', ''),
        hardware_version: _.get(searchParameters, 'hardware_version', '').split('_-_').filter(v => !_.isEmpty(v)),
        mp_batch_serial: _.get(searchParameters, 'mp_batch_serial', '').split('_-_').filter(v => !_.isEmpty(v)),
        custom_filter_ids: _.get(searchParameters, 'custom_filter_ids', '').split('_').map(s => parseInt(s)).filter(v => !_.isNil(v) && !_.isNaN(v)),
    })

    const sGeneralParams = useState<{ status?: number[], start_time?: string, end_time?: string, serial_number?: string, testing_station_id: string[], employee_id: string[], mp_batch_serial: string[], custom_filter_ids: number[] }>();

    const sSelectionsInfo = useState<OverviewSelections>();

    const sActiveTabIndex = useState(parseInt(_.get(searchParameters, 'tab_active_index', '0') || '0'));


    useLayoutEffect(() => {
        const onResize = () => {
            setHeight(window.innerHeight - searchBarRef.current!.offsetTop * 2);
        }

        window.addEventListener('resize', onResize);

        onResize();

        return () => {
            window.removeEventListener('resize', onResize);
        }
    }, []);

    const doRequestOverview = () => {
        sGeneralParams[1]({
            ...sSearch[0],
            start_time: sSearch[0].start_time ? moment(sSearch[0].start_time).format('YYYY-MM-DD HH:mm:ss') : undefined,
            end_time: sSearch[0].end_time ? moment(sSearch[0].end_time).format('YYYY-MM-DD HH:mm:ss') : undefined
        });

        // update url parameters
        const data: any = _.pick(sSearch[0], 'serial_number', 'start_time', 'end_time', 'status', 'testing_station_id', 'employee_id', 'never_pass', 'never_pass_other_station', 'stock_in', 'by_sequence', 'has_refurbishment_serial', 'mp_batch_serial', 'custom_filter_ids');
        data.start_time = data.start_time ? moment(data.start_time).format('YYYY-MM-DD HH:mm:ss') : null;
        data.end_time = data.end_time ? moment(data.end_time).format('YYYY-MM-DD HH:mm:ss') : null;

        if (data.status) {
            data.status = data.status.join('_')
        }
        if (data.testing_station_id) {
            data.testing_station_id = data.testing_station_id.join('_-_')
        }
        if (data.employee_id) {
            data.employee_id = data.employee_id.join('_-_')
        }
        data.has_refurbishment_serial = data.has_refurbishment_serial ? 't' : '';
        data.hardware_version = (sSearch[0].hardware_version || []).filter(v => !!v).join('_-_')
        data.mp_batch_serial = !_.isEmpty(data.mp_batch_serial) ? data.mp_batch_serial.join('_-_') : ''

        if (data.custom_filter_ids) {
            data.custom_filter_ids = data.custom_filter_ids.join('_')
        }

        setParentSearchParameter((ds: any) => ({ ...ds, ...data }));
    }

    useEffect(() => {
        dataReady[1](false);
        restRequest<OverviewSelections>('testings/overview_selections/' + id).then(info => {
            sSelectionsInfo[1](info);
            sSearch[1](s => ({
                ...s, testing_station_id: s.testing_station_id.filter(v => info.station_ids.includes(v)),
                employee_id: s.employee_id.filter(v => info.employees.includes(v)),
                hardware_version: s.hardware_version.filter(v => info.hardware_versions.includes(v)),
                never_pass_other_station: info.stations_in_same_order.find(v => v.id === s.never_pass_other_station) ? s.never_pass_other_station : undefined,
                mp_batch_serial: s.mp_batch_serial.filter(v => info.mp_batch_serials.find(mbs => mbs.uuid === v)),
                custom_filter_ids: s.custom_filter_ids.filter(v => info.data_filters.filter(df => df.id === v))
            }));

            doRequestOverview();

            changedId[1](id);

            dataReady[1](true);
        });

    }, [id])

    useEffect(() => {
        if (id == sSearch[0].never_pass_other_station) {
            sSearch[1](ss => ({ ...ss, never_pass_other_station: undefined }));
        }
    }, [id, sSearch[0].never_pass_other_station]);

    useEffect(() => {
        setParentSearchParameter((ds: any) => ({ ...ds, tab_active_index: sActiveTabIndex[0] }));
    }, [sActiveTabIndex[0]]);

    const filterStatuses: DropdownItemProps[] = [
        { text: 'Pass', value: 1, key: 1 },
        { text: 'Fail', value: 2, key: 2 },
        { text: 'Invalid', value: 3, key: 3 },
        { text: 'Stopped', value: 4, key: 4 }
    ]

    const filtersSationIds: DropdownItemProps[] = sSelectionsInfo[0]?.station_ids.sort().map(s => ({
        text: s,
        value: s,
        key: s
    })) || []

    const filtersSationInSameOrder: DropdownItemProps[] = [{ text: '', value: undefined, key: undefined } as any].concat(sSelectionsInfo[0]?.stations_in_same_order.sort().map(s => ({
        text: s.name,
        value: s.id,
        key: s.id
    })) || [])

    const filtersEmployees: DropdownItemProps[] = sSelectionsInfo[0]?.employees.sort().map(s => ({
        text: s,
        value: s,
        key: s
    })) || []

    const filtersHardwareVersions: DropdownItemProps[] = sSelectionsInfo[0]?.hardware_versions.sort().map(s => ({
        text: s,
        value: s,
        key: s
    })) || []

    const filterNeverPass: DropdownItemProps[] = [
        { text: '', value: undefined, key: undefined },
        { text: 'Yes', value: 1, key: 1 },
        { text: 'No', value: 2, key: 2 },
    ]

    const filterStockIn: DropdownItemProps[] = [
        { text: '', value: undefined, key: undefined },
        { text: 'Yes', value: 1, key: 1 },
        { text: 'No', value: 2, key: 2 },
    ]

    const filtersMPBatchSerials: DropdownItemProps[] = sSelectionsInfo[0]?.mp_batch_serials.map(s => ({
        text: s.serial,
        content: <Header size="small">{s.serial} <Header.Subheader>{s.start.split('T')[0]}~{s.end.split('T')[0]}</Header.Subheader></Header>,
        value: s.uuid,
        key: s.uuid
    })) || [];

    const customFilterOptions: DropdownItemProps[] = sSelectionsInfo[0]?.data_filters.sort().map(v => ({
        text: v.name,
        value: v.id,
        key: v.id,
        title: v.description || ''
    })) || []

    const panels = [
        { menuItem: 'General', render: () => dataReady[0] && <General id={changedId[0]} requestParams={sGeneralParams[0]} onLoading={sLoading[1]}></General> },
        { menuItem: 'Daily', render: () => dataReady[0] && <Daily id={changedId[0]} requestParams={sGeneralParams[0]} onLoading={sLoading[1]}></Daily> },
        { menuItem: 'Yield-Pareto', render: () => dataReady[0] && <ParetoTab id={changedId[0]} requestParams={sGeneralParams[0]} onLoading={sLoading[1]}></ParetoTab> },
        { menuItem: 'Pass-Error Statistic', render: () => dataReady[0] && <Measurements id={changedId[0]} requestParams={sGeneralParams[0]} onLoading={sLoading[1]}></Measurements> },
        { menuItem: 'Plot', render: () => dataReady[0] && <Plot id={changedId[0]} requestParams={sGeneralParams[0]} onLoading={sLoading[1]}></Plot> }
    ]

    return <Segment loading={sLoading[0]} style={{ margin: 0 }}>
        <div ref={searchBarRef} style={{ position: 'relative' }}>
            <div className={`searchBar`}>
                <form className="clearfix" onSubmit={e => {
                    e.preventDefault();

                    doRequestOverview();
                }}>
                    <Input size="mini" placeholder="Serials or File Name" value={sSearch[0].serial_number} onChange={(_, d) => sSearch[1](s => ({ ...s, serial_number: d.value.trim() }))}></Input>
                    <div className="ui mini input date" style={{ alignItems: 'center' }}>
                        <ReactDatePicker showTimeSelect
                            dateFormat="yyyy-MM-d HH:mm"
                            timeFormat="HH:mm"
                            selected={sSearch[0].start_time} onChange={(ds: Date) => sSearch[1](s => ({ ...s, start_time: ds }))}></ReactDatePicker>
                        <div style={{ padding: '0 0.5rem' }}> - </div>
                        <ReactDatePicker showTimeSelect
                            minDate={sSearch[0].start_time}
                            dateFormat="yyyy-MM-d HH:mm"
                            timeFormat="HH:mm"
                            selected={sSearch[0].end_time} onChange={(ds: Date) => sSearch[1](s => ({ ...s, end_time: ds }))}></ReactDatePicker>
                    </div>
                    {filtersMPBatchSerials.length > 1 && <MultipleDropdownFilter<string> label="Batch" value={sSearch[0].mp_batch_serial} onChange={v => sSearch[1](s => ({ ...s, mp_batch_serial: v }))} options={filtersMPBatchSerials} scrolling search />}

                    {!_.isEmpty(customFilterOptions) && <MultipleDropdownFilter<number> label="Custom Filter" value={sSearch[0].custom_filter_ids} onChange={value => sSearch[1](s => ({ ...s, custom_filter_ids: value }))} options={customFilterOptions} scrolling search />}

                    <MultipleDropdownFilter<number> label="Status" value={sSearch[0].status} onChange={value => sSearch[1](s => ({ ...s, status: value }))} options={filterStatuses} />

                    <DropdownFilter<number> label="Never Pass Current Station" value={sSearch[0].never_pass} onChange={value => sSearch[1](s => ({ ...s, never_pass: value }))} options={filterNeverPass} />

                    <DropdownFilter<number> label="Never Pass Other Station" value={sSearch[0].never_pass_other_station} onChange={value => sSearch[1](s => ({ ...s, never_pass_other_station: value }))} options={filtersSationInSameOrder} />

                    <DropdownFilter<number> label="Stock In" value={sSearch[0].stock_in} onChange={value => sSearch[1](s => ({ ...s, stock_in: value }))} options={filterStockIn} />

                    <MultipleDropdownFilter<string> label="Station" value={sSearch[0].testing_station_id} onChange={value => sSearch[1](s => ({ ...s, testing_station_id: value }))} options={filtersSationIds} />

                    <MultipleDropdownFilter<string> label="Operator" value={sSearch[0].employee_id} onChange={value => sSearch[1](s => ({ ...s, employee_id: value }))} options={filtersEmployees} search />

                    <MultipleDropdownFilter<string> label="Hardware Version" value={sSearch[0].hardware_version} onChange={value => sSearch[1](s => ({ ...s, hardware_version: value }))} options={filtersHardwareVersions} />

                    <DropdownFilter<string> label="By Sequence" value={sSearch[0].by_sequence || undefined} onChange={value => sSearch[1](s => ({ ...s, by_sequence: value }))} options={['', 'only_first', 'only_last'].map(v => ({ text: v.replaceAll('_', ' '), value: v || undefined, key: v }))} />

                    <Button content="Has Refurbishment" icon="check" size="mini" as="a" color={sSearch[0].has_refurbishment_serial ? 'yellow' : undefined} onClick={(e, d) => sSearch[1](s => ({ ...s, has_refurbishment_serial: !s.has_refurbishment_serial }))} className="form-item" />

                    <Button primary size="mini" type="submit" className="form-item">Filter</Button>

                    <Dropdown text="Export CSV" button className="mini olive">
                        <Dropdown.Menu>
                            <Dropdown.Item text="Sequences" onClick={() => {
                                window.open(hostAbsoluteUrl(`testings/overview_export_files/${id}?no_cache=${Date.now()}`))
                            }} />
                            <Dropdown.Divider />
                            <Dropdown.Item text='Measurements' onClick={() => {
                                window.open(hostAbsoluteUrl(`testings/overview_export_measurements/${id}?no_cache=${Date.now()}`))
                            }} />
                            <Dropdown.Item text='Batchs' onClick={() => {
                                window.open(hostAbsoluteUrl(`testings/export_batch/${id}?no_cache=${Date.now()}`))
                            }} />
                        </Dropdown.Menu>
                    </Dropdown>
                </form>
            </div>
            <Divider />
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', minHeight: height ? (height + 'px') : 'auto' }}>
            <Tab activeIndex={sActiveTabIndex[0]} onTabChange={(_, d) => sActiveTabIndex[1](d.activeIndex as number)} panes={panels} style={{ width: '100%' }}></Tab>
        </div>
    </Segment>
}


function General(props: { id: number, requestParams?: { status?: number[], serial_number?: string, start_time?: string, end_time?: string, testing_station_id: string[], employee_id: string[] }, onLoading: (status: boolean) => void }) {
    const sData = useState<OverviewGeneral>();
    const sUploadStatus = useState<UploadStatus[]>([]);
    const [searchParameters, setParentSearchParameter] = useContext(TestingVisualSearchParameterContext)!;


    useEffect(() => {
        props.onLoading(true);
        Promise.all([
            restRequest<UploadStatus[]>(`testings/upload_status/${props.id}`).then(result => {
                if (result.length > 0) {
                    if (result.length > 1) {
                        const total: UploadStatus = {
                            product: "",
                            order: "",
                            testing_station_name: "",
                            testing_station_id: "Total",
                            todos: 0,
                            errors: 0,
                            uploaded: 0,
                            updated_at: result[0].updated_at
                        };
                        for (const s of result) {
                            total.todos += s.todos;
                            total.errors += s.errors;
                            total.uploaded += s.uploaded;
                        }
                        sUploadStatus[1]([total, ...result]);
                    } else {
                        sUploadStatus[1](result);
                    }

                } else {
                    sUploadStatus[1]([]);
                }
            }),
            restRequest<OverviewGeneral>(`testings/overview_general/${props.id}`, props.requestParams || {}, 'post').then(sData[1])
        ]).finally(() => props.onLoading(false));
    }, [props.id, props.requestParams, props.onLoading])

    if (!sData[0]) {
        return <div>Loading</div>;
    }

    const { sequences, total_number, fail_serials, unique_serial_number } = sData[0];

    return <div style={{ paddingTop: '1rem' }}>
        <Segment style={{ position: 'relative' }}>
            <h3>Sequences</h3>
            <div style={{ position: 'absolute', right: '0.5rem', top: '0.5rem' }}>Total: {total_number}</div>
            <Table celled singleLine definition>
                <Table.Header>
                    <Table.Row>
                        <th>Status</th>
                        <th>Number</th>
                        <th>Number Percentage</th>
                        <th>Average Testing Time (Second)</th>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {['Pass', 'Fail', 'Invalid', 'Stopped'].map((name, index) => {
                        const data = sequences[index + 1] || { count: 0, times: 0 };
                        return <Table.Row key={index}>
                            <td><b>{name}</b> &nbsp;
                                <a className="ui mini right floated compact button" onClick={e => {
                                    e.preventDefault();
                                    props.onLoading(true);
                                    restRequest<string[]>(`testings/sequences/${props.id}/${index + 1}`, props.requestParams || {}, 'post').then(serials => {
                                        downloadCsv(serials.map(v => ({ v })), { v: 'File Name' }, name.toLowerCase() + '_sequences.csv');
                                    }).finally(() => props.onLoading(false));
                                }}>CSV</a>
                            </td>
                            <td>{data.count}</td>
                            <td>{(data.count * 100.0 / total_number).toFixed(2) || 0}%</td>
                            <td>{data.count > 0 ? (data.times / data.count).toFixed(1) : 0}</td>
                        </Table.Row>
                    })}
                </Table.Body>
            </Table>
        </Segment>

        <Segment>
            <h3>Unique Serial Number</h3>
            <Table celled singleLine definition>
                <Table.Header>
                    <Table.Row>
                        <th>Status</th>
                        <th>Number</th>
                        <th>Percentage</th>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    <Table.Row>
                        <td><b>Pass</b> &nbsp;
                            <a className="ui mini right floated compact button" onClick={e => {
                                e.preventDefault();
                                props.onLoading(true);
                                restRequest<string[]>(`testings/pass_serials/${props.id}`, props.requestParams || {}, 'post').then(serials => {
                                    downloadCsv(serials.map(v => ({ v })), { v: 'Serial Number' }, 'pass_serials.csv');
                                }).finally(() => props.onLoading(false));
                            }}>CSV</a>
                        </td>
                        <td>{unique_serial_number - fail_serials.length}</td>
                        <td>{(100.0 * (unique_serial_number - fail_serials.length) / unique_serial_number).toFixed(2)} %</td>
                    </Table.Row>
                    <Table.Row>
                        <td><b>Fail</b> &nbsp;
                            <a className="ui mini right floated compact button" onClick={e => {
                                e.preventDefault();
                                downloadCsv(fail_serials.map(v => ({ v })), { v: 'Serial Number' }, 'fail_serials.csv');
                            }}>CSV</a>
                        </td>
                        <td>{fail_serials.length}</td>
                        <td>{(100.0 * fail_serials.length / unique_serial_number).toFixed(2)} %</td>
                    </Table.Row>
                </Table.Body>
            </Table>
        </Segment>

        {sUploadStatus[0].length > 0 && <Segment>
            <h3>Upload Status</h3>
            <Table celled singleLine definition>
                <Table.Header>
                    <Table.Row>
                        <th>Station ID</th>
                        <th>Total</th>
                        <th>Todo Count</th>
                        <th>Uploaded Count</th>
                        <th>Error Count</th>
                        <th>Updated at</th>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {sUploadStatus[0].map((s, i) => <tr key={i}>
                        <td>{s.testing_station_id}</td>
                        <td>{s.todos + s.errors + s.uploaded}</td>
                        <td>{s.todos}</td>
                        <td>{s.uploaded}</td>
                        <td>{s.errors}</td>
                        <td>{formatDateTime(s.updated_at)}</td>
                    </tr>)}
                </Table.Body>
            </Table>
        </Segment>}
    </div>
}


function Daily(props: { id: number, requestParams?: { status?: number[], serial_number?: string, testing_station_id: string[], employee_id: string[] }, onLoading: (status: boolean) => void }) {
    const sData = useState<OverviewDaily>();
    const [searchParameters, setParentSearchParameter] = useContext(TestingVisualSearchParameterContext)!;


    const sOverheadValve = useState(parseInt(_.get(searchParameters, 't_1_overhead', '300') || '300'));
    const sRefresh = useState(0);

    const startRefreshRef = useRef(_.debounce(() => {
        sRefresh[1](v => v + 1);
    }, 600));

    useEffect(() => {
        props.onLoading(true);
        restRequest<OverviewDaily>(`testings/overview_daily/${props.id}`, { ...(props.requestParams || {}), overhead_valve: sOverheadValve[0] }, 'post').then(sData[1]).finally(() => props.onLoading(false));
    }, [props.id, props.requestParams, sRefresh[0], props.onLoading])

    if (!sData[0]) {
        return <div>Loading</div>;
    }

    const days = Object.keys(sData[0]).sort((a, b) => a > b ? 1 : -1);

    const csvData = [
        ['Date', 'Total Testing Time(h)', 'Average Testing Time(s)',
            'Total Testing Time for Pass(h)', 'Average Testing Time for Pass(s)',
            'Total Testing Time for Fail(h)', 'Average Testing Time for Fail(s)',
            'Total Testing Time for Invalid(h)', 'Average Testing Time for Invalid(s)',
            'Total Testing Time for Stopped(h)', 'Average Testing Time for Stopped(s)',
            'Total of Overhead(h)', 'Average of Overhead(s)', 'Median of Overhead(s)',
            'Break Time(h)', 'Total(h)',
            'Total', 'Pass', 'Fail', 'Invalid', 'Stopped', 'Lost(%)'
        ],

        ...days.map(day => {
            const s = sData[0]![day];
            const sumOverhead = _.sum(s.overheads);
            const total = s.pass + s.fail + s.invalid + s.stopped;

            return [day, (s.testing_times / 3600.0).toFixed(2), (s.testing_times / total).toFixed(0),
                (s.testing_times_pass / 3600.0)!.toFixed(2), s.pass > 0 ? (s.testing_times_pass / s.pass).toFixed(0) : 0,
                (s.testing_times_fail / 3600.0)!.toFixed(2), s.fail > 0 ? (s.testing_times_fail / s.fail).toFixed(0) : 0,
                (s.testing_times_invalid / 3600.0)!.toFixed(2), s.invalid > 0 ? (s.testing_times_invalid / s.invalid).toFixed(0) : 0,
                (s.testing_times_stopped / 3600.0)!.toFixed(2), s.stopped > 0 ? (s.testing_times_stopped / s.stopped).toFixed(0) : 0,
                (sumOverhead / 3600).toFixed(2), (_.mean(s.overheads) || 0).toFixed(0), (d3.median(s.overheads) || 0).toFixed(0),
                (s.breaks / 3600.0).toFixed(2), ((s.testing_times + sumOverhead + s.breaks) / 3600).toFixed(2),
                total, s.pass, s.fail, s.invalid, s.stopped, ((s.fail + s.invalid + s.stopped) * 100.0 / total).toFixed(2)
            ]
        })
    ]

    return <div style={{ paddingTop: '1rem', overflow: 'auto' }}>
        <Input size="mini" label="overhead valve (second)" type="number" value={sOverheadValve[0]} onChange={(e, d) => {
            const val = parseInt(d.value) || 300;
            sOverheadValve[1](val);

            setParentSearchParameter((ps: any) => ({ ...ps, t_1_overhead: val }));
            startRefreshRef.current();
        }}></Input>

        <a onClick={e => {
            e.preventDefault();
            const headersArray = ['Date', 'Total Testing Time(h)', 'Average Testing Time(s)',
                'Total Testing Time for Pass(h)', 'Average Testing Time for Pass(s)',
                'Total Testing Time for Fail(h)', 'Average Testing Time for Fail(s)',
                'Total Testing Time for Invalid(h)', 'Average Testing Time for Invalid(s)',
                'Total Testing Time for Stopped(h)', 'Average Testing Time for Stopped(s)',
                'Total of Overhead(h)', 'Average of Overhead(s)', 'Median of Overhead(s)',
                'Break Time(h)', 'Total(h)',
                'Total', 'Pass', 'Fail', 'Invalid', 'Stopped', 'Lost(%)'
            ];
            const keys: string[] = headersArray.map((v, i) => `k_${i}`);
            const headers: { [key: string]: string } = {};
            keys.forEach((k, i) => {
                headers[k] = headersArray[i];
            });

            const datas = days.map(day => {
                const s = sData[0]![day];
                const sumOverhead = _.sum(s.overheads);
                const total = s.pass + s.fail + s.invalid + s.stopped;

                const arrayValue = [day, (s.testing_times / 3600.0).toFixed(2), (s.testing_times / total).toFixed(0),
                    (s.testing_times_pass / 3600.0)!.toFixed(2), s.pass > 0 ? (s.testing_times_pass / s.pass).toFixed(0) : 0,
                    (s.testing_times_fail / 3600.0)!.toFixed(2), s.fail > 0 ? (s.testing_times_fail / s.fail).toFixed(0) : 0,
                    (s.testing_times_invalid / 3600.0)!.toFixed(2), s.invalid > 0 ? (s.testing_times_invalid / s.invalid).toFixed(0) : 0,
                    (s.testing_times_stopped / 3600.0)!.toFixed(2), s.stopped > 0 ? (s.testing_times_stopped / s.stopped).toFixed(0) : 0,
                    (sumOverhead / 3600).toFixed(2), (_.mean(s.overheads) || 0).toFixed(0), (d3.median(s.overheads) || 0).toFixed(0),
                    (s.breaks / 3600.0).toFixed(2), ((s.testing_times + sumOverhead + s.breaks) / 3600).toFixed(2),
                    total, s.pass, s.fail, s.invalid, s.stopped, ((s.fail + s.invalid + s.stopped) * 100.0 / total).toFixed(2)
                ];

                const values: { [key: string]: any } = {};

                arrayValue.forEach((v, i) => {
                    values[keys[i]] = v;
                });

                return values;
            });

            downloadCsv(datas, headers, 'daily_overview.csv')
        }} className="ui mini right floated secondary button" target="_blank" >Download CSV</a>

        <MyBarChart xValues={csvData.slice(1).map(vs => vs[0]) as string[]} yData={csvData.slice(1).map(vs => [vs[16], vs[17], vs[18], vs[19], vs[20], (vs[18] as number) + (vs[19] as number) + (vs[20] as number)]) as number[][]} labels={['Total', 'Pass', 'Fail', 'Invalid', 'Stopped', 'Lost']} />

        <Table celled singleLine selectable>
            <Table.Header>
                <Table.Row>
                    <th rowSpan={3}>Date</th>
                    <th colSpan={10}>Testing Time</th>
                    <th colSpan={3}>Overhead</th>
                    <th rowSpan={3}>Break Time(h)</th>
                    <th rowSpan={3}>Total(h)</th>
                    <th colSpan={6}>Number</th>
                </Table.Row>
                <Table.Row>
                    <th colSpan={2} style={{ borderLeft: '1px solid rgba(34,36,38,.1)' }}>All</th>
                    <th colSpan={2}>Pass</th>
                    <th colSpan={2}>Fail</th>
                    <th colSpan={2}>Invalid</th>
                    <th colSpan={2}>Stopped</th>

                    <th rowSpan={2}>Total(h)</th>
                    <th rowSpan={2}>Average(s)</th>
                    <th rowSpan={2}>Median(s)</th>

                    <th rowSpan={2}>Total</th>
                    <th rowSpan={2}>Pass</th>
                    <th rowSpan={2}>Fail</th>
                    <th rowSpan={2}>Invalid</th>
                    <th rowSpan={2}>Stopped</th>
                    <th rowSpan={2}>Lost(%)</th>
                </Table.Row>
                <Table.Row>
                    <th style={{ borderLeft: '1px solid rgba(34,36,38,.1)' }}>Total(h)</th>
                    <th>Average(s)</th>

                    <th>Total(h)</th>
                    <th>Average(s)</th>

                    <th>Total(h)</th>
                    <th>Average(s)</th>

                    <th>Total(h)</th>
                    <th>Average(s)</th>

                    <th>Total(h)</th>
                    <th>Average(s)</th>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {csvData.slice(1).map((csvRow) => <Table.Row key={csvRow[0]}>
                    {csvRow.map((d: any, tdIndex) => <td key={tdIndex}>{d}</td>)}
                </Table.Row>)}
            </Table.Body>
        </Table>
    </div>
}


type PeratoPlotData = OverviewMeasurement['measurements'][number] & { value: number };


function ParetoTab(props: { id: number, requestParams?: { status?: number[], serial_number?: string, start_time?: string, end_time?: string, testing_station_id: string[], employee_id: string[] }, onLoading: (status: boolean) => void }) {
    const [searchParameters, setParentSearchParameter] = useContext(TestingVisualSearchParameterContext)!;

    const sIsUnit = useState(_.get(searchParameters, 't_2_g_is_unit', '1') === '1');
    const sParetoData = useState(_.get(searchParameters, 't_2_g_data', '1_2').split('_').map(v => parseInt(v)));

    const sData = useState<OverviewMeasurement>();
    const sParetoStatistics = useState<PeratoPlotData[]>([]);

    useEffect(() => {
        props.onLoading(true);
        restRequest<OverviewMeasurement>(`testings/overview_measurements_pareto_data/${props.id}`, { is_units: sIsUnit[0], ...(props.requestParams || {}) }, 'post').then(sData[1]).finally(() => props.onLoading(false));
    }, [props.id, props.requestParams, sIsUnit[0], props.onLoading])


    useEffect(() => {
        const includeFail = sParetoData[0].includes(1);
        const includeInvalid = sParetoData[0].includes(2);
        const paretoStatistics = sData[0]?.measurements.map(m => ({ ...m, value: (includeFail ? m.fail_number : 0) + (includeInvalid ? m.invalid_number : 0) })) || [];
        sParetoStatistics[1](paretoStatistics);
    }, [sData[0], sParetoData[0]]);

    useEffect(() => {
        setParentSearchParameter((ps: any) => ({ ...ps, tab_path: 't_2', t_2_g_is_unit: sIsUnit[0] ? '1' : '0', t_2_g_data: sParetoData[0].length > 0 ? sParetoData[0].join('_') : undefined }));
    }, [sIsUnit[0], sParetoData[0]])

    return <div style={{ paddingTop: '1rem' }}>
        <Form as="div">
            <Form.Group inline>
                <div className="field inline">
                    <label>Group By: </label>
                    <Select value={sIsUnit[0]} options={[{ text: 'Units', value: true, key: 0 }, { text: 'Measurements', value: false, key: 1 }]} onChange={(e, d) => sIsUnit[1](!!d.value)}></Select>
                </div>

                <div className="field inline">
                    <label>Pareto Data: </label>
                    <Dropdown labeled inline button multiple value={sParetoData[0]} options={[{ text: 'FAIL', value: 1, key: 1 }, { text: 'INVALID', value: 2, key: 2 }]} onChange={(e, d) => sParetoData[1](d.value as number[] || [])}></Dropdown>
                </div>

                <div style={{flex: 1, textAlign: 'right'}}>
                    <Button onClick={() => {
                        window.open(hostAbsoluteUrl('testings/export_overview_measurements_pareto_data/' + props.id + '?no_cache=' + Date.now() + '&searchData=' + toUrlQueryString({...props.requestParams, is_units: sIsUnit[0], include_invalid: sParetoData[0].includes(2)})))
                    }} className="secondary" size="mini">Download CSV</Button>
                </div>
            </Form.Group>
        </Form>

        {sParetoStatistics[0].length > 0 && <ParetoPlot id={props.id} requestParams={props.requestParams} data={sParetoStatistics[0]} showOverlayedLink={!sIsUnit[0]} />}
    </div>
}

function Measurements(props: { id: number, requestParams?: { status?: number[], serial_number?: string, start_time?: string, end_time?: string, testing_station_id: string[], employee_id: string[] }, onLoading: (status: boolean) => void }) {
    const [searchParameters, setParentSearchParameter] = useContext(TestingVisualSearchParameterContext)!;

    const sSortBy = useState<{ name?: 'ascending' | 'descending', pass_number?: 'ascending' | 'descending', fail_number?: 'ascending' | 'descending', invalid_number?: 'ascending' | 'descending', total?: 'ascending' | 'descending', average_time?: 'ascending' | 'descending', median_time?: 'ascending' | 'descending' }>({
        fail_number: 'descending'
    });

    const sIsUnit = useState(_.get(searchParameters, 't_3_g_is_unit', '1') === '1');

    useEffect(() => {
        setParentSearchParameter((ps: any) => ({ ...ps, tab_path: 't_3', t_3_g_is_unit: sIsUnit[0] ? '1' : '0' }));
    }, [sIsUnit[0]])
    const sIncludeNotTestedMeasurements = useState(false);

    const sData = useState<OverviewMeasurement>();

    const measurementInfosRef = useRef<{ [measurementKey: string]: { max_channel: number, data_type: number } }>({});

    useEffect(() => {
        props.onLoading(true);
        restRequest<OverviewMeasurement>(`testings/overview_measurements/${props.id}`, { is_units: sIsUnit[0], include_not_tested_measurements: sIncludeNotTestedMeasurements[0], ...(props.requestParams || {}) }, 'post').then(sData[1]).finally(() => props.onLoading(false));
    }, [props.id, props.requestParams, sIsUnit[0], sIncludeNotTestedMeasurements[0], props.onLoading])

    const getMeasurementInfo = useCallback((measurementKey: string, getValue: (info: { max_channel: number, data_type: number }) => void) => {
        const info = measurementInfosRef.current[measurementKey];
        if (info) {
            getValue(info);
        } else {
            restRequest<{ max_channel: number, data_type: number }>(`testings/overview_measurement_info/${props.id}`, { measurement_key: measurementKey }, 'post').then(val => {
                measurementInfosRef.current[measurementKey] = val;

                getValue(val);
            })
        }
    }, [props.id]);

    if (!sData[0]) {
        return <div>Loading</div>;
    }

    if (!sData[0].measurements) {
        return <div>Loading</div>
    }

    const { measurements } = sData[0];

    let result = measurements;

    if (sSortBy[0]?.name) {
        result = result.sort((a, b) => {
            return compareMeasurementName(a.measurement_name, b.measurement_name, sSortBy[0]?.name === 'descending')
        });
    } else if (sSortBy[0]?.pass_number) {
        let g = 1;
        let l = -1;
        if (sSortBy[0]?.pass_number === 'descending') {
            g = -1;
            l = 1;
        }
        result = result.sort((a, b) => a.pass_number > b.pass_number ? g : l);
    } else if (sSortBy[0]?.fail_number) {
        let g = 1;
        let l = -1;
        if (sSortBy[0]?.fail_number === 'descending') {
            g = -1;
            l = 1;
        }
        result = result.sort((a, b) => a.fail_number > b.fail_number ? g : l);
    } else if (sSortBy[0]?.invalid_number) {
        let g = 1;
        let l = -1;
        if (sSortBy[0]?.invalid_number === 'descending') {
            g = -1;
            l = 1;
        }
        result = result.sort((a, b) => a.invalid_number > b.invalid_number ? g : l);
    } else if (sSortBy[0]?.total) {
        let g = 1;
        let l = -1;
        if (sSortBy[0]?.total === 'descending') {
            g = -1;
            l = 1;
        }
        result = result.sort((a, b) => a.total > b.total ? g : l);
    } else if (sSortBy[0]?.average_time) {
        let g = 1;
        let l = -1;
        if (sSortBy[0]?.average_time === 'descending') {
            g = -1;
            l = 1;
        }
        result = result.sort((a, b) => a.average_time > b.average_time ? g : l);
    } else if (sSortBy[0]?.median_time) {
        let g = 1;
        let l = -1;
        if (sSortBy[0]?.median_time === 'descending') {
            g = -1;
            l = 1;
        }
        result = result.sort((a, b) => a.median_time > b.median_time ? g : l);
    }


    return <div style={{ paddingTop: '1rem' }}>
        <Form as="div">
            <Form.Group inline>
                <div className="field inline">
                    <label>Group By: </label>
                    <Select value={sIsUnit[0]} options={[{ text: 'Units', value: true, key: 0 }, { text: 'Measurements', value: false, key: 1 }]} onChange={(e, d) => sIsUnit[1](!!d.value)}></Select>
                </div>

                {/* <div className="field inline">
                    <Checkbox label="Include Not Tested Measurements" value={1} onClick={(e, d) => sIncludeNotTestedMeasurements[1](d.checked || false)}></Checkbox>
                </div> */}
            </Form.Group>
        </Form>

        <Table selectable celled sortable>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell rowSpan="2" onClick={() => sSortBy[1](s => ({ name: s?.name === 'descending' ? 'ascending' : 'descending' }))} sorted={sSortBy[0]?.name}>Name</Table.HeaderCell>

                    <Table.HeaderCell rowSpan="2" onClick={() => sSortBy[1](s => ({ total: s?.total === 'descending' ? 'ascending' : 'descending' }))} sorted={sSortBy[0]?.total}>Total</Table.HeaderCell>
                    <Table.HeaderCell colSpan="2" onClick={() => sSortBy[1](s => ({ pass_number: s?.pass_number === 'descending' ? 'ascending' : 'descending' }))} sorted={sSortBy[0]?.pass_number}>Pass</Table.HeaderCell>
                    <Table.HeaderCell colSpan="2" onClick={() => sSortBy[1](s => ({ fail_number: s?.fail_number === 'descending' ? 'ascending' : 'descending' }))} sorted={sSortBy[0]?.fail_number}>Fail</Table.HeaderCell>
                    <Table.HeaderCell colSpan="2" onClick={() => sSortBy[1](s => ({ invalid_number: s?.invalid_number === 'descending' ? 'ascending' : 'descending' }))} sorted={sSortBy[0]?.invalid_number}>Invalid</Table.HeaderCell>

                    <Table.HeaderCell colSpan="2">Testing Time(ms)</Table.HeaderCell>
                </Table.Row>
                <Table.Row>
                    <Table.HeaderCell style={{ borderLeft: '1px solid rgba(34,36,38,.1)' }}>Number</Table.HeaderCell>
                    <Table.HeaderCell>Percentage</Table.HeaderCell>

                    <Table.HeaderCell>Number</Table.HeaderCell>
                    <Table.HeaderCell>Percentage</Table.HeaderCell>

                    <Table.HeaderCell>Number</Table.HeaderCell>
                    <Table.HeaderCell>Percentage</Table.HeaderCell>

                    <Table.HeaderCell onClick={() => sSortBy[1](s => ({ average_time: s?.average_time === 'descending' ? 'ascending' : 'descending' }))} sorted={sSortBy[0]?.average_time}>Average</Table.HeaderCell>
                    <Table.HeaderCell onClick={() => sSortBy[1](s => ({ median_time: s?.median_time === 'descending' ? 'ascending' : 'descending' }))} sorted={sSortBy[0]?.median_time}>Median</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {result.map(m => {
                    return <Table.Row key={m.measurement_key}>
                        <Table.Cell>{m.measurement_name} {[DATA_TYPE_SCALAR, DATA_TYPE_ARRAY, DATA_TYPE_VECTOR].includes(m.data_type)
                            && <>
                                &nbsp;&nbsp;
                                <Button onClick={e => {
                                    const params = _.clone(props.requestParams) as any;
                                    params.measurement_key = m.measurement_key;
                                    params.measurement_name = m.measurement_name;
                                    params.export_file_name = m.export_file_name;
                                    params.dataType = m.data_type;
                                    params.productName = document.title;

                                    const $btn = $(e.target).addClass('loading');

                                    getMeasurementInfo(m.measurement_key, info => {
                                        params.maxChannel = info.max_channel;
                                        window.open(`/overview_parameters_over_time/${props.id}/${window.btoa(encodeURIComponent(JSON.stringify(params)))}`);
                                        $btn.removeClass('loading');
                                    })
                                }} size="mini" basic compact target="_blank">Over Time</Button>

                                <Button onClick={e => {
                                    const params = _.clone(props.requestParams) as any;
                                    params.measurement_key = m.measurement_key;
                                    params.measurement_name = m.measurement_name;
                                    params.export_file_name = m.export_file_name;
                                    params.dataType = m.data_type;
                                    params.productName = document.title;

                                    const $btn = $(e.target).addClass('loading');

                                    getMeasurementInfo(m.measurement_key, info => {
                                        params.maxChannel = info.max_channel;
                                        window.open(`/overview_parameters_for_histogram/${props.id}/${window.btoa(encodeURIComponent(JSON.stringify(params)))}`);
                                        $btn.removeClass('loading');
                                    })
                                }} size="mini" basic compact target="_blank">Histogram</Button>
                            </>}</Table.Cell>

                        <Table.Cell>{m.total}</Table.Cell>
                        <Table.Cell>{m.pass_number}</Table.Cell>
                        <Table.Cell>{(m.pass_number * 100.0 / m.total).toFixed(1)}%</Table.Cell>
                        <Table.Cell>{m.fail_number}</Table.Cell>
                        <Table.Cell>{(m.fail_number * 100.0 / m.total).toFixed(1)}%</Table.Cell>
                        <Table.Cell>{m.invalid_number}</Table.Cell>
                        <Table.Cell>{(m.invalid_number * 100.0 / m.total).toFixed(1)}%</Table.Cell>

                        <Table.Cell>{m.average_time.toFixed(0)}</Table.Cell>
                        <Table.Cell>{m.median_time}</Table.Cell>
                    </Table.Row>
                })}
            </Table.Body>
        </Table>
    </div>
}



function ParetoPlot(props: { id: number, requestParams: any, data: PeratoPlotData[], height?: number, showOverlayedLink?: boolean }) {
    const { data: rawData, showOverlayedLink } = props;
    const svgRootRef = useRef<HTMLDivElement>(null);
    const [failMeasurements, setFailMeasurements] = useState<string[]>([]);

    const margin = ({ top: 20, right: 80, right_text: 30, bottom: 160, left: 50 })

    const [currentInfo, setCurrentInfo] = useState<{ name: string, export_file_name: string, count: number, percentage: number, isShow: boolean }>({ isShow: false } as any);

    const sData = useState<typeof rawData>([]);
    const sErrors = useState<number[]>([]);
    const sPercentages = useState<number[]>([]);
    const sCumulativePercents = useState<number[]>([]);

    useEffect(() => {
        const data = rawData.filter(d => d.value > 0).sort((a, b) => a.value > b.value ? -1 : (a.value != b.value ? 1 : (a.measurement_name > b.measurement_name ? -1 : 1)));
        const total = d3.sum(data.map(d => d.value));
        const measurements: [string, string][] = [];
        const errors: number[] = [];
        const percentages: number[] = [];
        const cumulativePercents: number[] = [];
        data.forEach((d, i) => {
            let acP = 0;
            for (let j = 0; j <= i; j++) {
                acP += data[j].value;
            }

            measurements.push([d.measurement_name, d.export_file_name]);
            errors.push(d.value);
            percentages.push(d.value / total);
            cumulativePercents.push(acP / total);
        });

        sData[1](data);
        sErrors[1](errors);
        sPercentages[1](percentages);
        sCumulativePercents[1](cumulativePercents);
    }, [rawData])

    useEffect(() => {
        const width = svgRootRef.current!.offsetWidth;
        const height = props.height || 500;

        const svg = d3.select(svgRootRef.current!)
            .append("svg")
            .attr("viewBox", [0, 0, width, height] as any);

        const names = sData[0].map(d => d.measurement_name + '_' + d.export_file_name);

        const x = d3.scaleBand().domain(names).range([margin.left, width - margin.right]);

        const yDomain = [0, _.sum(sErrors[0])];
        const y = d3.scaleLinear().domain(yDomain).range([height - margin.bottom, margin.top]);
        const y2 = d3.scaleLinear().domain([0, 1.0]).range([height - margin.bottom, margin.top]);

        const xAxis = (g: any) => {
            g.attr("transform", `translate(0, ${height - margin.bottom})`).call(d3.axisBottom(x).tickFormat((d, i) => sData[0][i].measurement_name));
        }

        svg.append("g")
            .attr("class", "x-axis")
            .call(xAxis).selectAll("text")
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .attr("transform", "rotate(-65)");

        const yAxis = (g: any) => {
            g.attr("transform", `translate(${margin.left},0)`).call(d3.axisLeft(y).tickValues(y.ticks().filter(t => Number.isInteger(t))).tickFormat(d3.format('d')));
        }

        svg.append("g")
            .attr("class", "y-axis")
            .call(yAxis);

        const y2Axis = (g: any) => {
            g.attr("transform", `translate(${width - margin.right},0)`).call(d3.axisRight(y2).tickFormat((d: any) => (100.0 * d).toFixed(1) + '%'));
        }

        svg.append("g")
            .attr("class", "y2-axis")
            .call(y2Axis);

        const barWidth = x.bandwidth() * 0.618;
        const barMargin = x.bandwidth() - barWidth;

        svg.append('g')
            .selectAll('rest.bar')
            .data(sData[0])
            .join('rect')
            .attr('class', 'bar')
            .attr('fill', 'steelblue')
            .attr('stroke', 'none')
            .attr('x', (d, i) => (x(names[i]) as number) + barMargin / 2)
            .attr('y', d => y(d.value))
            .attr('width', barWidth)
            .attr('height', (d, i) => height - margin.bottom - y(d.value));

        const pLine = d3.line<number>().x((d, i) => (x(names[i]) as number) + barMargin / 2 + barWidth / 2).y((d, i) => y2(d));

        svg.append("path")
            .attr('class', 'data-line')
            .attr("fill", "none")
            .attr("stroke", 'purple')
            .attr("stroke-width", 1)
            .attr("d", pLine(sCumulativePercents[0]) as any);

        const bisectDate = d3.bisector(function (d) { return d }).left;
        const eightyPercentageIndex = bisectDate(sCumulativePercents[0], 0.8);
        const eightyPercentageX = x(names[eightyPercentageIndex]) as number + barMargin / 2;
        svg.append("line")
            .attr('x1', eightyPercentageX)
            .attr('y1', 0)
            .attr('x2', eightyPercentageX)
            .attr('y2', height - margin.bottom)
            .attr('fill', 'none')
            .attr('stroke', 'red')
            .attr('stroke-dasharray', '3');

        svg.append('text')
            .attr('dominant-baseline', 'center')
            .style("text-anchor", "middle")
            .attr("transform", `rotate(-90, 20, ${(height - margin.bottom) / 2})`)
            .attr('x', 20)
            .attr('y', (height - margin.bottom) / 2)
            .style('line-height', '20px')
            .text('Number of Errors');

        svg.append('text')
            .attr('dominant-baseline', 'center')
            .style("text-anchor", "middle")
            .attr("transform", `rotate(90, ${width - margin.right_text}, ${(height - margin.bottom) / 2})`)
            .attr('x', width - margin.right_text)
            .attr('y', (height - margin.bottom) / 2)
            .style('line-height', '20px')
            .text('Cumulative Percent of Total');

        setFailMeasurements(rawData.filter((o, i) => i <= eightyPercentageIndex).map(d => d.measurement_key));

        //focus
        const xPositions = sData[0].map((o, i) => (x(names[i]) as number) + barMargin / 2);
        const focus = svg.append('g').style('display', 'none');

        focus.append('line')
            .attr('id', 'focusLineX')
            .attr('class', 'focusLine');
        focus.append('line')
            .attr('id', 'focusLineY')
            .attr('class', 'focusLine');
        focus.append('line')
            .attr('id', 'focusLineY2')
            .attr('class', 'focusLine');
        focus.append('circle')
            .attr('id', 'focusCircle')
            .attr('r', 5)
            .attr('class', 'circle focusCircle');
        focus.append('circle')
            .attr('id', 'focusCircle2')
            .attr('r', 5)
            .attr('class', 'circle focusCircle');

        svg.append('rect')
            .attr('class', 'overlay')
            .attr('width', width)
            .attr('height', height)
            .on('mouseover', function () { focus.style('display', null); })
            .on('mouseout', function () {
                focus.style('display', 'none');
                setCurrentInfo(info => ({ ...info, isShow: false }))
            })
            .on('mousemove', function (event) {
                const mouse = d3.pointer(event);
                const mouseX = mouse[0];
                let i = bisectDate(xPositions, mouseX); // returns the index to the current data item

                const d0 = xPositions[i - 1];
                const d1 = xPositions[i];
                // work out which date value is closest to the mouse
                if (mouseX - d0 < d1 - mouseX) {
                    i -= 1;
                }

                if (i >= 0 && i < sData[0].length) {
                    setCurrentInfo({ name: sData[0][i].measurement_name, export_file_name: sData[0][i].export_file_name, count: sData[0][i].value, percentage: sCumulativePercents[0][i], isShow: true });

                    const xv = xPositions[i] + barWidth / 2;
                    const yv = y(sData[0][i].value);
                    const yv2 = y2(sCumulativePercents[0][i]);

                    focus.select('#focusLineX')
                        .attr('x1', xv).attr('y1', y(yDomain[0]))
                        .attr('x2', xv).attr('y2', Math.min(yv, yv2));

                    focus.select('#focusLineY')
                        .attr('x1', margin.left).attr('y1', yv)
                        .attr('x2', xv).attr('y2', yv);

                    focus.select('#focusLineY2')
                        .attr('x1', xv).attr('y1', yv2)
                        .attr('x2', width - margin.right).attr('y2', yv2);

                    focus.select('#focusCircle')
                        .attr('cx', xv)
                        .attr('cy', yv);

                    focus.select('#focusCircle2')
                        .attr('cx', xv)
                        .attr('cy', yv2);
                }
            });

        return () => {
            svg.remove();
        }

    }, [sData[0], sErrors[0], sPercentages[0], sCumulativePercents[0], props.height]);

    const measurementInfosRef = useRef<{ [measurementKey: string]: { max_channel: number, data_type: number } }>({});

    const getMeasurementInfo = useCallback((measurementKey: string, getValue: (info: { max_channel: number, data_type: number }) => void) => {
        const info = measurementInfosRef.current[measurementKey];
        if (info) {
            getValue(info);
        } else {
            restRequest<{ max_channel: number, data_type: number }>(`testings/overview_measurement_info/${props.id}`, { measurement_key: measurementKey }, 'post').then(val => {
                measurementInfosRef.current[measurementKey] = val;

                getValue(val);
            })
        }
    }, [props.id]);

    return <div>
        <div style={{ paddingLeft: '2rem', visibility: currentInfo.isShow ? 'visible' : 'hidden' }}>
            {<div className="ui label" style={{ width: 'auto' }}><span style={{ color: 'steelblue' }}>Count: <b>{currentInfo.count}</b></span>, &nbsp;
                <span style={{ color: 'purple' }}>Percentage: <b>{currentInfo.percentage && (100 * currentInfo.percentage).toFixed(1)}%</b></span>, &nbsp;
                Measurement: <b>{currentInfo.name} {currentInfo.export_file_name && <>({currentInfo.export_file_name})</>}</b></div>}
        </div>
        <div ref={svgRootRef}></div>
        {showOverlayedLink && <a className="ui green compact mini button inverted" style={{ marginLeft: margin.left + 'px' }} href={`detail?fail_measurements=${encodeURIComponent(JSON.stringify(failMeasurements))}`} target="_blank" title="Show all fail sequences">Overlayed Details</a>}

        <Table selectable celled sortable>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Name</Table.HeaderCell>

                    <Table.HeaderCell>Number of Errors</Table.HeaderCell>
                    <Table.HeaderCell>Percent of Total</Table.HeaderCell>
                    <Table.HeaderCell>Cumulative Percent of Total</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {sData[0].map((d, i) => {
                    return <Table.Row key={d.measurement_key}>
                        <Table.Cell>{d.measurement_name} {[DATA_TYPE_SCALAR, DATA_TYPE_ARRAY, DATA_TYPE_VECTOR].includes(d.data_type)
                            && <>
                                &nbsp;&nbsp;
                                <Button onClick={e => {
                                    const params = _.clone(props.requestParams) as any;
                                    params.measurement_key = d.measurement_key;
                                    params.measurement_name = d.measurement_name;
                                    params.export_file_name = d.export_file_name;
                                    params.dataType = d.data_type;
                                    params.productName = document.title;

                                    const $btn = $(e.target).addClass('loading');

                                    getMeasurementInfo(d.measurement_key, info => {
                                        params.maxChannel = info.max_channel;
                                        window.open(`/overview_parameters_over_time/${props.id}/${window.btoa(encodeURIComponent(JSON.stringify(params)))}`);
                                        $btn.removeClass('loading');
                                    })
                                }} size="mini" basic compact target="_blank">Over Time</Button>

                                <Button onClick={e => {
                                    const params = _.clone(props.requestParams) as any;
                                    params.measurement_key = d.measurement_key;
                                    params.measurement_name = d.measurement_name;
                                    params.export_file_name = d.export_file_name;
                                    params.dataType = d.data_type;
                                    params.productName = document.title;

                                    const $btn = $(e.target).addClass('loading');

                                    getMeasurementInfo(d.measurement_key, info => {
                                        params.maxChannel = info.max_channel;
                                        window.open(`/overview_parameters_for_histogram/${props.id}/${window.btoa(encodeURIComponent(JSON.stringify(params)))}`);
                                        $btn.removeClass('loading');
                                    })
                                }} size="mini" basic compact target="_blank">Histogram</Button>
                            </>}</Table.Cell>

                        <Table.Cell>{sErrors[0][i]}</Table.Cell>
                        <Table.Cell>{(sPercentages[0][i] * 100).toFixed(2)}%</Table.Cell>
                        <Table.Cell>{(sCumulativePercents[0][i] * 100).toFixed(2)}%</Table.Cell>
                    </Table.Row>
                })}
            </Table.Body>
        </Table>
    </div>
}


type MeasurementBasicInfo = { name: string, key: string, export_file_name: string, data_type: number };
function Plot(props: { id: number, requestParams?: { status?: number[], serial_number?: string, start_time?: string, end_time?: string, testing_station_id: string[], employee_id: string[] }, onLoading: (status: boolean) => void }) {
    const [searchParameters, setParentSearchParameter] = useContext(TestingVisualSearchParameterContext)!;
    const sMeasurementNameAndTypes = useState<MeasurementBasicInfo[]>();
    const sSelectedMeasurement = useState<MeasurementBasicInfo>();
    const sDelayTestId = useState<number>(props.id);
    const sDelayRequestParams = useState<any>(props.requestParams);
    const sCurrentInfo = useState<MeasurementPlotInfo>();
    const sMeasurementReadyKey = useState('');
    const refSelectedMeasurementKey = useRef(_.get(searchParameters, 't_4_selected_measuremet_key', ''))

    useEffect(() => {
        props.onLoading(true);
        requestMeasurementNameAndType(props.id + '').then(measurementNameAndTypes => {
            sMeasurementNameAndTypes[1](measurementNameAndTypes);
            sDelayTestId[1](props.id);
            sDelayRequestParams[1](props.requestParams);

            let selectedMeasurement;
            if (measurementNameAndTypes) {
                if (sSelectedMeasurement[0]) {
                    selectedMeasurement = measurementNameAndTypes.find(s => s.key == sSelectedMeasurement[0]?.key);
                    if (!selectedMeasurement) {
                        sSelectedMeasurement[1](undefined);
                    }
                } else if (refSelectedMeasurementKey.current) {
                    sSelectedMeasurement[1](measurementNameAndTypes.find(s => s.key == refSelectedMeasurementKey.current));
                    refSelectedMeasurementKey.current = ''
                }
            }
        }).finally(() => {
            props.onLoading(false);
        });
    }, [props.id, props.requestParams]);

    useEffect(() => {
        setParentSearchParameter((ps: any) => ({ ...ps, tab_path: 't_4', t_4_selected_measuremet_key: sSelectedMeasurement[0]?.key }))
    }, [sSelectedMeasurement[0]])

    useEffect(() => {
        if (sSelectedMeasurement[0] && [DATA_TYPE_SCALAR, DATA_TYPE_ARRAY, DATA_TYPE_VECTOR].includes(sSelectedMeasurement[0].data_type) && sDelayTestId[0]) {
            props.onLoading(true);
            const measurement_key = sSelectedMeasurement[0].key;
            restRequest<MeasurementPlotInfo>('testings/overview_plot_measurement_info/' + sDelayTestId[0], { measurement_key }, 'post', undefined, `overview_plot_measurement_info/${sDelayTestId[0]}/${measurement_key}`).then(d => {
                d.limits = d.limits.map(JSON.parse as any);
                sCurrentInfo[1](d);
                sMeasurementReadyKey[1](measurement_key);
            }).finally(() => {
                props.onLoading(false);
            });
        }
    }, [sSelectedMeasurement[0], sDelayTestId[0]])



    return <div style={{ paddingTop: '1rem', position: 'relative' }}>
        <Form style={{ position: 'absolute', top: '30px' }}>
            <Form.Group>
                <Form.Field inline>
                    <label>Measurement:</label>
                    <Dropdown scrolling search button onChange={(e, d) => {
                        sSelectedMeasurement[1](sMeasurementNameAndTypes[0] && sMeasurementNameAndTypes[0].find(s => s.key === d.value));
                    }} value={sSelectedMeasurement[0] ? sSelectedMeasurement[0].key : ''} options={sMeasurementNameAndTypes[0]?.filter(m => m.data_type === DATA_TYPE_ARRAY || m.data_type === DATA_TYPE_VECTOR || m.data_type === DATA_TYPE_SCALAR).map((m, i) => ({ text: m.name, value: m.key, key: m.key + m.export_file_name })) || []} className="compact mini"></Dropdown>
                </Form.Field>
            </Form.Group>
        </Form>
        {sSelectedMeasurement[0] && sCurrentInfo[0] && (sSelectedMeasurement[0].key == sMeasurementReadyKey[0]) && (sSelectedMeasurement[0].data_type === DATA_TYPE_VECTOR ?
            <VectorPlot testing_id={sDelayTestId[0].toString()} measurementKey={sSelectedMeasurement[0].key} info={sCurrentInfo[0]} requestParams={sDelayRequestParams[0]} onLoading={props.onLoading}></VectorPlot> :
            <ScatterPlot testing_id={sDelayTestId[0].toString()} measurementKey={sSelectedMeasurement[0].key} dataType={sSelectedMeasurement[0].data_type} info={sCurrentInfo[0]} requestParams={sDelayRequestParams[0]} onLoading={props.onLoading}></ScatterPlot>
        )}
    </div>
}